import { useEffect, useState } from "react";

const STORAGE_KEY = "video-call-low-power";

/**
 * Low-power mode for the live coaching call.
 *
 * When enabled, the Daily prebuilt iframe is launched with conservative
 * camera constraints and simulcast encodings: ~640×360 @ 20fps, top
 * bitrate ~400 kbps. This dramatically reduces CPU/GPU load on older
 * laptops, mobile Safari, and devices that already have other heavy tabs
 * (Zoom, OBS, etc.) open.
 *
 * The value is read ONCE when the iframe URL is built, toggling mid-call
 * does not retroactively apply. Users need to leave & rejoin for the new
 * setting to take effect, which is acceptable as a "set before joining"
 * preference.
 */
export function getLowPowerMode(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STORAGE_KEY) === "1";
}

export function setLowPowerMode(enabled: boolean) {
  if (typeof window === "undefined") return;
  if (enabled) window.localStorage.setItem(STORAGE_KEY, "1");
  else window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("lowpowermode:change"));
}

export function useLowPowerMode(): [boolean, (v: boolean) => void] {
  const [enabled, setEnabled] = useState<boolean>(getLowPowerMode);
  useEffect(() => {
    const onChange = () => setEnabled(getLowPowerMode());
    window.addEventListener("lowpowermode:change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("lowpowermode:change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  return [enabled, (v: boolean) => setLowPowerMode(v)];
}

/**
 * Daily prebuilt accepts a `dailyConfig` URL param (URL-encoded JSON).
 * Returns the query-string fragment to append, or "" when disabled.
 */
export function lowPowerDailyConfigParam(enabled: boolean): string {
  if (!enabled) return "";
  const config = {
    userMediaVideoConstraints: {
      width: { ideal: 640, max: 854 },
      height: { ideal: 360, max: 480 },
      frameRate: { ideal: 20, max: 24 },
    },
    camSimulcastEncodings: [
      { maxBitrate: 120_000, scaleResolutionDownBy: 2, maxFramerate: 15 },
      { maxBitrate: 400_000, scaleResolutionDownBy: 1, maxFramerate: 20 },
    ],
  };
  return `&dailyConfig=${encodeURIComponent(JSON.stringify(config))}`;
}